import {
  Dispatch,
  SetStateAction,
  ComponentType as ReactComponentType,
} from "react"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { SETTINGS_SCOPE } from "@tfg/types/constants/communication-studio.constant"
import { ProductSheetLanguage } from "@tfg/types/constants/language.constants"
import { ComponentContent } from "@tfg/types/schemas/communication-studio/components"
import {
  ComponentType,
  ComponentTypesEnum,
} from "@tfg/types/types/communication-studio/components.types"
import { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import { BulletedListRenderer } from "../../Languages/components/BulletedListRenderer"
import { EcoscoreRenderer } from "../../Languages/components/EcoScoreRenderer"
import { DynamicTextEditor } from "../DynamicTextEditor"
import { LogoRenderer } from "../renderers/LogoRenderer"
import { ProductAttributesRenderer } from "../renderers/ProductAttributesRenderer"
import {
  LogoConfigurationRequiredSchema,
  ProductAttributesConfigurationRequiredSchema,
  ComponentContentRequiredSchema,
  EcoScoreConfigurationRequiredSchema,
} from "../schemas/componentValidationSchemas"

export interface ValidationResult<T = unknown> {
  success: boolean
  data?: T
  errors?: z.ZodError
}

export const validateWithSchema = <T>(
  data: unknown,
  schema: z.ZodType<T> | undefined
): ValidationResult<T> => {
  if (!schema) {
    return { success: true, data: data as T }
  }

  const result = schema.safeParse(data)

  if (result.success) {
    return {
      success: true,
      data: result.data,
    }
  }
  return {
    success: false,
    errors: result.error,
  }
}

export const getValidationErrorMessages = (
  errors: z.ZodError | undefined
): Record<string, string[]> => {
  if (!errors) return {}

  return errors.issues.reduce(
    (acc, error) => {
      const path = error.path.join(".") || "root"
      if (!acc[path]) {
        acc[path] = []
      }
      acc[path].push(error.message)
      return acc
    },
    {} as Record<string, string[]>
  )
}

export const hasValidationErrors = (
  validation: ValidationResult | undefined
): boolean => {
  return validation ? !validation.success : false
}

// ============================================================================
// Schema-based validation helpers
// ============================================================================

/**
 * Checks if a schema is required (not undefined)
 * A schema being defined means the component requires that type of data
 */
export const isDataRequired = (schema: z.ZodTypeAny | undefined): boolean => {
  return schema !== undefined
}

/**
 * Validates data against a schema and returns whether it's valid
 * Returns true if schema is undefined (no requirement) or if data passes validation
 */
export const isDataValid = <T>(
  data: unknown,
  schema: z.ZodType<T> | undefined
): boolean => {
  if (!schema) return true // No schema means no requirement
  const result = schema.safeParse(data)
  return result.success
}

export const isDataEmpty = <T>(
  data: unknown,
  schema: z.ZodType<T> | undefined
): boolean => {
  if (!schema) return false // No schema means not required, so never "empty"
  if (data === null || data === undefined) return true
  if (typeof data === "object" && Object.keys(data).length === 0) return true
  if (Array.isArray(data) && data.length === 0) return true
  return false
}

export const isComponentDataReady = (
  content: unknown,
  configurations: unknown,
  contentSchema?: z.ZodTypeAny,
  configurationsSchema?: z.ZodTypeAny
): boolean => {
  const contentRequired = isDataRequired(contentSchema)
  const configurationsRequired = isDataRequired(configurationsSchema)
  if (contentRequired && !isDataValid(content, contentSchema)) {
    return false
  }

  if (
    configurationsRequired &&
    !isDataValid(configurations, configurationsSchema)
  ) {
    return false
  }

  return true
}

export interface BaseRendererProps {
  textContent: ComponentContent | null
  setTextContent: Dispatch<SetStateAction<ComponentContent | null>>
}

export interface RendererCommonProps extends BaseRendererProps {
  updateTextContent?: (
    blockIndex: number,
    textIndex: number,
    newValue: string
  ) => void
  componentType?: ComponentType
  language?: ProductSheetLanguage
  configurations?: Partial<ContentFilteringProps> | null
  updateModalConfigurations?: (
    configurations: Partial<ContentFilteringProps> | null
  ) => void
}

export interface ComponentValidationResults {
  content?: ValidationResult
  configurations?: ValidationResult<Partial<ContentFilteringProps>>
}

export interface ComponentRendererMetadata {
  type: ComponentType
  renderer: ReactComponentType<any>
  description: string

  contentSchema?: z.ZodTypeAny

  configurationsSchema?: z.ZodType<Partial<ContentFilteringProps>>

  matcher?: (type: ComponentType) => boolean

  propsMapper?: (
    commonProps: RendererCommonProps,
    validations: ComponentValidationResults,
    metadata?: ComponentRendererMetadata
  ) => any
}

// ============================================================================
// COMPONENT RENDERER REGISTRY
// ============================================================================

const ComponentRenderComponents = [
  ComponentTypesEnum.BULLETED_LIST,
  ComponentTypesEnum.TEXT_PARAGRAPH_V2,
  ComponentTypesEnum.ECOSCORE,
  ComponentTypesEnum.LOGO,
  ComponentTypesEnum.PRODUCT_ATTRIBUTES,
] as const

type ComponentRenderComponent = (typeof ComponentRenderComponents)[number]

export const useGetComponetRenderRegistry = () => {
  const { t } = useTranslation()
  return {
    [ComponentTypesEnum.BULLETED_LIST]: {
      type: ComponentTypesEnum.BULLETED_LIST,
      renderer: BulletedListRenderer,
      description: "Renders bulleted list content with rich text editor",
      contentSchema: ComponentContentRequiredSchema,
      propsMapper: (
        props: RendererCommonProps,
        validations: ComponentValidationResults
      ) => ({
        textContent: validations.content?.data ?? props.textContent,
        setTextContent: props.setTextContent,
        validationErrors: validations.content?.errors,
      }),
    },

    [ComponentTypesEnum.TEXT_PARAGRAPH_V2]: {
      type: ComponentTypesEnum.TEXT_PARAGRAPH_V2,
      renderer: DynamicTextEditor,
      description: "Renders paragraph content with dynamic text editor",
      contentSchema: ComponentContentRequiredSchema,
      propsMapper: (
        props: RendererCommonProps,
        validations: ComponentValidationResults
      ) => ({
        value: validations.content?.data ?? props.textContent ?? {},
        onChange: props.setTextContent,
        contentType: "text",
        validationErrors: validations.content?.errors,
      }),
    },
    [ComponentTypesEnum.ECOSCORE]: {
      type: ComponentTypesEnum.ECOSCORE,
      renderer: EcoscoreRenderer,
      description: t(
        "communicationStudio:tabs.content.componentModal.ecoscore.description"
      ),
      configurationsSchema: EcoScoreConfigurationRequiredSchema,
      propsMapper: (
        props: RendererCommonProps,
        validations: ComponentValidationResults,
        metadata?: ComponentRendererMetadata
      ) => ({
        ecoScoreDataSource: props.configurations?.ecoScoreDataSource,
        description: metadata?.description,
        validationErrors: validations.configurations?.errors,
        updateModalConfigurations: props.updateModalConfigurations,
      }),
    },
    [ComponentTypesEnum.LOGO]: {
      type: ComponentTypesEnum.LOGO,
      renderer: LogoRenderer,
      description: t(
        "communicationStudio:tabs.content.componentModal.descriptions.LOGO.description"
      ),
      configurationsSchema: LogoConfigurationRequiredSchema,
      configurationScopes: [SETTINGS_SCOPE.CONTENT_FILTERING],
      propsMapper: (
        props: RendererCommonProps,
        validations: ComponentValidationResults,
        metadata?: ComponentRendererMetadata
      ) => {
        const configurations = props.configurations

        return {
          logoFile: configurations?.logoFile,
          description: metadata?.description,
          validationErrors: validations.configurations?.errors,
          updateModalConfigurations: props.updateModalConfigurations,
        }
      },
    },
    [ComponentTypesEnum.PRODUCT_ATTRIBUTES]: {
      type: ComponentTypesEnum.PRODUCT_ATTRIBUTES,
      renderer: ProductAttributesRenderer,
      description: t(
        "communicationStudio:tabs.content.componentModal.descriptions.PRODUCT_ATTRIBUTES.description"
      ),
      configurationsSchema: ProductAttributesConfigurationRequiredSchema,
      configurationScopes: [SETTINGS_SCOPE.CONTENT_FILTERING],
      propsMapper: (
        props: RendererCommonProps,
        validations: ComponentValidationResults,
        metadata?: ComponentRendererMetadata
      ) => ({
        primaryProductAttributes:
          props.configurations?.primaryProductAttributes,
        secondaryProductAttributes:
          props.configurations?.secondaryProductAttributes,
        updateModalConfigurations: props.updateModalConfigurations,
        description: metadata?.description,
        validationErrors: validations.configurations?.errors,
      }),
    },
  }
}

export const useGetComponentRenderer = (
  componentType: ComponentType | null
): ComponentRendererMetadata | null => {
  const componentRendererRegistry = useGetComponetRenderRegistry()
  if (
    !componentType ||
    !componentRendererRegistry ||
    !ComponentRenderComponents.includes(componentType)
  ) {
    return null
  }
  let parsedComponentType: ComponentRenderComponent | undefined =
    ComponentRenderComponents.find((c) => c === componentType)
  if (!parsedComponentType) {
    return null
  }
  return componentRendererRegistry[parsedComponentType]
}

// this needs to go here to avoid parsing script from removing them as they are accessed dynamically
// t("communicationStudio:tabs.content.components.ECOSCORE")
// t("communicationStudio:tabs.content.components.TEXT_HEADER")
// t("communicationStudio:tabs.content.components.TEXT_PARAGRAPH")
// t("communicationStudio:tabs.content.components.RECYCLED_MATERIALS_INFO")
// t("communicationStudio:tabs.content.components.PACKAGING_INFO")
// t("communicationStudio:tabs.content.components.TRIMAN_FOOTER")
// t("communicationStudio:tabs.content.components.TRIMAN_FOOTER_POWERED_BY")
// t("communicationStudio:tabs.content.components.ACTIVITY_LIST")
// t("communicationStudio:tabs.content.components.ACTIVITY_LIST_WITH_ICONS")
// t("communicationStudio:tabs.content.components.POWERED_BY")
// t("communicationStudio:tabs.content.components.HEADER_WITH_PRODUCT")
// t("communicationStudio:tabs.content.components.HEADER_WITH_PRODUCT_NAME")
// t("communicationStudio:tabs.content.components.BULLETED_LIST")
// t("communicationStudio:tabs.content.components.TEXT_PARAGRAPH_V2")
// t("communicationStudio:tabs.content.components.LOGO")
// t("communicationStudio:tabs.content.components.GENERIC_PASSPORT_TITLE")
// t("communicationStudio:tabs.content.components.PRODUCT_ATTRIBUTES")
// t("communicationStudio:tabs.content.components.ORGANIZATION_TITLE")
// t("communicationStudio:tabs.content.components.PRODUCT_IMAGE")
// t("communicationStudio:tabs.content.productAttributes.options.brand")
// t("communicationStudio:tabs.content.productAttributes.options.collection")
// t("communicationStudio:tabs.content.productAttributes.options.brandReference")
// t("communicationStudio:tabs.content.productAttributes.options.purchaseOrderReference")
// t("communicationStudio:tabs.content.productAttributes.options.name")
// t("communicationStudio:tabs.content.productAttributes.options.color")
